import { useForm, getFormProps } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod/v4";
import { invariantResponse } from "@epic-web/invariant";
import { json, type ActionFunctionArgs } from "@remix-run/node";
import { redirect, useFetcher, useFetchers } from "@remix-run/react";
import { IconDeviceDesktop, IconMoon, IconSun } from "@tabler/icons-react";
import { useSyncExternalStore } from "react";
import { ServerOnly } from "remix-utils/server-only";
import { z } from "zod";
import { useRequestInfo } from "#app/utils/request-info.ts";
import {
  DEFAULT_THEME,
  THEME_COOKIE_NAME,
  THEME_STORAGE_KEY,
} from "#app/utils/theme-bootstrap.ts";
import { type Theme, setTheme } from "#app/utils/theme.server.ts";

export type ThemePreference = Theme | "system";

const ThemeFormSchema = z.object({
  theme: z.enum(["system", "light", "dark"]),
  // this is useful for progressive enhancement
  redirectTo: z.string().optional(),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: ThemeFormSchema,
  });

  invariantResponse(submission.status === "success", "Invalid theme received");

  const { theme, redirectTo } = submission.value;

  const responseInit = {
    headers: { "set-cookie": setTheme(theme) },
  };
  if (redirectTo) {
    return redirect(redirectTo, responseInit);
  } else {
    return json({ result: submission.reply() }, responseInit);
  }
}

export function ThemeSwitch({
  userPreference,
}: {
  userPreference?: Theme | null;
}) {
  const fetcher = useFetcher<typeof action>();
  const requestInfo = useRequestInfo();

  const [form] = useForm({
    id: "theme-switch",
    lastResult: fetcher.data?.result,
  });

  const optimisticMode = useOptimisticThemeMode();
  const preference: ThemePreference =
    optimisticMode ?? userPreference ?? "system";
  const options = [
    { value: "system" as const, label: "System", icon: IconDeviceDesktop },
    { value: "light" as const, label: "Light", icon: IconSun },
    { value: "dark" as const, label: "Dark", icon: IconMoon },
  ];
  const currentIndex = options.findIndex(({ value }) => value === preference);
  const currentOption = options[currentIndex] ?? options[0]!;
  const nextOption =
    options[(currentIndex + 1) % options.length] ?? options[0]!;
  const CurrentIcon = currentOption.icon;

  return (
    <fetcher.Form
      method="POST"
      {...getFormProps(form)}
      action="/resources/theme-switch"
    >
      <ServerOnly>
        {() => (
          <input type="hidden" name="redirectTo" value={requestInfo.path} />
        )}
      </ServerOnly>
      <button
        type="submit"
        name="theme"
        value={nextOption.value}
        aria-label={`${currentOption.label} theme. Switch to ${nextOption.label}`}
        title={`${currentOption.label} theme`}
        className="inline-flex size-10 cursor-pointer items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-xs transition-colors hover:bg-zinc-100 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#535EA4]/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
        onClick={() => persistTheme(nextOption.value)}
      >
        <CurrentIcon className="size-5" aria-hidden="true" />
      </button>
    </fetcher.Form>
  );
}

export function persistTheme(theme: ThemePreference) {
  if (typeof window === "undefined") return;
  // Write cookie + localStorage together so a refresh before the theme-switch
  // action response cannot paint a stale SSR theme (dark→light flash).
  if (theme === "system") {
    localStorage.removeItem(THEME_STORAGE_KEY);
    document.cookie = `${THEME_COOKIE_NAME}=; path=/; max-age=-1`;
    const resolved = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : DEFAULT_THEME;
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(resolved);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(resolved);
  } else {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.cookie = `${THEME_COOKIE_NAME}=${encodeURIComponent(theme)}; path=/; max-age=31536000; SameSite=Lax`;
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
  }
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to. Cookie remains the source of truth otherwise —
 * do not read localStorage here or hydration will flash against SSR.
 */
function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) =>
    f.formAction?.startsWith("/resources/theme-switch"),
  );

  if (themeFetcher?.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema,
    });

    if (submission.status === "success") {
      return submission.value.theme;
    }
  }
}

/**
 * @returns the user's explicit preference, or system when unset.
 */
export function useThemePreference(): ThemePreference {
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  return optimisticMode ?? requestInfo.userPrefs.theme ?? "system";
}

const systemThemeQuery = "(prefers-color-scheme: dark)";

function subscribeToSystemTheme(onChange: () => void) {
  const media = window.matchMedia(systemThemeQuery);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

function getSystemThemeSnapshot() {
  return window.matchMedia(systemThemeQuery).matches;
}

/** Returns the resolved light/dark theme and follows live OS changes. */
export function useTheme(): Theme {
  const preference = useThemePreference();
  const systemIsDark = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemThemeSnapshot,
    () => false,
  );
  return preference === "system"
    ? systemIsDark
      ? "dark"
      : DEFAULT_THEME
    : preference;
}
